'use client';

/**
 * Login Form Component
 * 
 * Core component for customer authentication.
 * Can be styled by themes using className props and CSS variables.
 */

import { useState, useCallback, useRef, type FormEvent } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useCustomerAuth } from './CustomerAuthProvider';
import { createClient } from '@/lib/supabase/client';
import { validateRedirectPath } from '@/lib/security/validate-redirect';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { TurnstileWidget, type TurnstileRef } from '@/components/ui/TurnstileWidget';

// ============================================================================
// Types
// ============================================================================

export interface LoginFormProps {
  /** Callback after successful login */
  onSuccess?: () => void;
  /** Redirect URL after login (default: /account) */
  redirectTo?: string;
  /** Show "Create account" link */
  showRegisterLink?: boolean;
  /** Register page URL */
  registerUrl?: string;
  /** Show "Forgot password" link */
  showForgotPasswordLink?: boolean;
  /** Forgot password page URL */
  forgotPasswordUrl?: string;
  /** Show social auth buttons */
  showSocialAuth?: boolean;
  /** Custom class name for styling */
  className?: string;
  /** Custom title */
  title?: string;
  /** Custom subtitle */
  subtitle?: string;
}

// ============================================================================
// Component
// ============================================================================

export function LoginForm({
  onSuccess,
  redirectTo = '/account',
  showRegisterLink = true,
  registerUrl = '/register',
  showForgotPasswordLink = true,
  forgotPasswordUrl = '/forgot-password',
  showSocialAuth = true,
  className = '',
  title = 'Welcome Back',
  subtitle = 'Sign in to your account',
}: LoginFormProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { signIn, signInWithOAuth, error, clearError } = useCustomerAuth();

  const localeMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = localeMatch?.[1] || 'en';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormError(null);
    clearError();

    // Validation
    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }

    if (!password) {
      setFormError('Password is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const profile = await signIn({
        email: email.trim(),
        password,
        captchaToken: turnstileToken || undefined,
      });

      // signIn returns null when MFA verification is required (AAL1 session).
      // Check MFA status and redirect to the appropriate 2FA page.
      if (!profile) {
        const supabase = createClient();
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const hasVerifiedTOTP = factorsData?.totp?.some(
          (f: { status: string }) => f.status === 'verified'
        );

        const safeRedirect = redirectTo ? validateRedirectPath(redirectTo, `/${locale}/account`) : '';
        const redirectParam = safeRedirect ? `?redirect=${encodeURIComponent(safeRedirect)}` : '';

        if (hasVerifiedTOTP) {
          router.push(`/${locale}/verify-2fa${redirectParam}`);
        } else {
          router.push(`/${locale}/setup-2fa${redirectParam}`);
        }
        return;
      }

      onSuccess?.();

      if (redirectTo) {
        router.push(validateRedirectPath(redirectTo, `/${locale}/account`));
      }
    } catch (err) {
      console.error('[LoginForm] Login failed:', err);
      // Reset Turnstile — tokens are single-use
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuthLogin = async (provider: 'google') => {
    try {
      // Pass redirectTo to OAuth flow so user returns to correct page after auth
      await signInWithOAuth(provider, redirectTo);
    } catch (err) {
      console.error('[LoginForm] OAuth failed:', err);
    }
  };

  const displayError = formError || error?.message;
  // Only disable during active submit - not during initial auth check (isLoading).
  // Auth check can take 1–2s; blocking interaction on load is poor UX.
  const isDisabled = isSubmitting;

  return (
    <div className={`login-form ${className}`}>
      {/* Header */}
      <div className="login-form__header mb-8">
        <h1 className="login-form__title text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          {title}
        </h1>
        <p className="login-form__subtitle text-base text-stone-500 mt-2">
          {subtitle}
        </p>
      </div>

      {/* Error Alert */}
      {displayError && (
        <div className="login-form__error mb-6 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{displayError}</p>
        </div>
      )}

      {/* Social Auth */}
      {showSocialAuth && (
        <>
          <div className="login-form__social space-y-3">
            <button
              type="button"
              onClick={() => handleOAuthLogin('google')}
              disabled={isDisabled}
              className="login-form__social-btn w-full flex items-center justify-center gap-3 h-12 px-4 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 hover:border-stone-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <GoogleIcon className="h-5 w-5" />
              <span className="text-sm font-medium text-stone-700">Continue with Google</span>
            </button>
          </div>

          {/* Divider */}
          <div className="login-form__divider relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-sm text-stone-400 font-medium">
                or continue with email
              </span>
            </div>
          </div>
        </>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="login-form__form space-y-5">
        {/* Email */}
        <div className="login-form__field">
          <label
            htmlFor="login-email"
            className="login-form__label block text-sm font-medium text-stone-700 mb-2"
          >
            Email address
          </label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            disabled={isDisabled}
            className="login-form__input w-full h-12 px-4 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Password */}
        <div className="login-form__field">
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="login-password"
              className="login-form__label block text-sm font-medium text-stone-700"
            >
              Password
            </label>
            {showForgotPasswordLink && (
              <Link
                href={forgotPasswordUrl}
                className="login-form__forgot-link text-sm font-medium text-amber-600 hover:text-amber-700 hover:underline transition-colors"
              >
                Forgot password?
              </Link>
            )}
          </div>
          <div className="relative">
            <input
              id="login-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={isDisabled}
              className="login-form__input w-full h-12 px-4 pr-12 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Turnstile Bot Protection */}
        <TurnstileWidget
          ref={turnstileRef}
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
          className="flex justify-center"
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isDisabled}
          className="login-form__submit w-full h-12 rounded-xl bg-stone-900 text-white font-semibold hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-stone-900/10"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Signing in...
            </span>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      {/* Register Link */}
      {showRegisterLink && (
        <p className="login-form__register mt-8 text-center text-sm text-stone-500">
          Don&apos;t have an account?{' '}
          <Link
            href={registerUrl}
            className="font-semibold text-amber-600 hover:text-amber-700 hover:underline transition-colors"
          >
            Create account
          </Link>
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default LoginForm;
